import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building, Users, CheckCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/superadmin/dashboard`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      icon: Building,
      label: 'Total Companies',
      value: stats?.totalCompanies || 0,
      color: 'text-brand-primary',
      bg: 'bg-blue-50'
    },
    {
      icon: CheckCircle,
      label: 'Active Companies',
      value: stats?.activeCompanies || 0,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      icon: Users,
      label: 'Total Users',
      value: stats?.totalUsers || 0,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      icon: TrendingUp,
      label: 'Platform Activity',
      value: `${stats?.platformActivity || 0}%`,
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    }
  ];

  return (
    <div className="space-y-6" data-testid="superadmin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-brand-semibold text-brand mb-2">SuperAdmin Dashboard</h1>
        <p className="text-brand-text-secondary">Platform-wide overview and metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-brand hover:shadow-lg transition-all">
              <CardContent className="padding-brand">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-brand-medium text-brand-text-secondary mb-1">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-brand-semibold text-brand">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bg} p-3 rounded-brand`}>
                    <Icon className={`h-8 w-8 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <Card className="card-brand">
        <CardHeader>
          <CardTitle className="text-brand-primary">Platform Activity</CardTitle>
        </CardHeader>
        <CardContent className="padding-brand">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-brand">
              <div>
                <p className="font-brand-medium text-brand">Companies Created Today</p>
                <p className="text-sm text-brand-text-secondary">New registrations</p>
              </div>
              <span className="text-2xl font-brand-semibold text-brand-primary">
                {stats?.companiesCreatedToday || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-brand">
              <div>
                <p className="font-brand-medium text-brand">Active Users Today</p>
                <p className="text-sm text-brand-text-secondary">Currently logged in</p>
              </div>
              <span className="text-2xl font-brand-semibold text-green-600">
                {stats?.activeUsersToday || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-brand-medium text-brand">Total Data Uploads</p>
                <p className="text-sm text-brand-text-secondary">Across all companies</p>
              </div>
              <span className="text-2xl font-brand-semibold text-purple-600">
                {stats?.totalUploads || 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
